"use client"

import React, { useState, useEffect } from "react"
import { Tree } from "@blueprintjs/core"

function groupBy(list, keyGetter) {
  const map = new Map()
  list.forEach((item) => {
    const key = keyGetter(item)
    const collection = map.get(key)
    if (!collection) {
      map.set(key, [item])
    } else {
      collection.push(item)
    }
  })
  return map
}

export default function ImagePicker(props) {
  const [currentReply, setCurrentReply] = useState({ ReplyLink: "" })
  const [nodes, setNodes] = useState([])
  const [isMobile, setIsMobile] = useState(false)
  const [showImageModal, setShowImageModal] = useState(false)
  const [modalImageSrc, setModalImageSrc] = useState("")
  const [zoomLevel, setZoomLevel] = useState(1)

  // Añadir un componente de lista simple para dispositivos móviles
  // Insertar después de la declaración de useState y antes del useEffect

  // Función para abrir imagen en modal con zoom
  // const openImageModal = (imageSrc) => {
  //   setModalImageSrc(imageSrc)
  //   setShowImageModal(true)
  //   setZoomLevel(1)
  // }

  // Función para cerrar el modal
  const closeImageModal = () => {
    setShowImageModal(false)
    setModalImageSrc("")
    setZoomLevel(1)
  }

  // Función para hacer zoom
  const handleZoom = (delta) => {
    setZoomLevel(prev => Math.max(0.5, Math.min(3, prev + delta)))
  }

  // Componente Modal para vista ampliada de imagen
  const ImageModal = () => {
    if (!showImageModal) return null
    
    return (
      <div 
        className="image-modal-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center"
        }}
        onClick={closeImageModal}
      >
       
        {/* Contenedor de imagen con zoom */}
        <div 
          className="modal-image-container"
          style={{
            maxWidth: "90%",
            maxHeight: "80%",
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={modalImageSrc}
            alt="Vista ampliada"
            style={{
              transform: `scale(${zoomLevel})`,
              transition: "transform 0.2s ease",
              maxWidth: zoomLevel <= 1 ? "100%" : "none",
              maxHeight: zoomLevel <= 1 ? "100%" : "none",
              cursor: zoomLevel > 1 ? "grab" : "default"
            }}
            draggable={false}
          />
        </div>

        {/* Indicador de zoom */}
        <div 
          style={{
            position: "absolute",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "white",
            padding: "5px 15px",
            borderRadius: "15px",
            fontSize: "14px"
          }}
        >
          Zoom: {Math.round(zoomLevel * 100)}%
        </div>
      </div>
    )
  }

  const ImageList = ({ items, onSelect }) => {
    return (
      <div className="mobile-image-list">
        {items.map((category) => (
          <div key={category.id} className="image-category">
            <h4 className="category-title">{category.label}</h4>
            <div className="image-grid">
              {category.childNodes.map((item) => (
                <div
                  key={item.id}
                  className={`image-item ${currentReply.Id === item.nodeData.Id ? "selected" : ""}`}
                  onClick={() => onSelect(item.nodeData)}
                >
                  <div className="image-name">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Detectar si es dispositivo móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)

    return () => {
      window.removeEventListener("resize", checkMobile)
    }
  }, [])

  const imageReplies = (props.quickReplies || []).filter((x) => x.ReplyType.includes("image"))
  const grouped = Array.from(groupBy(imageReplies, (x) => x.CategoryName.String))
  const imageNodes = grouped
    .map(([group, items]) => {
      return {
        id: group,
        hasCaret: true,
        label: group === "" ? "(Default)" : group,
        childNodes: items
          .map((i) => {
            return {
              id: i.Id,
              icon: "media",
              label: i.FileName.String,
              nodeData: i,
            }
          })
          .sort((a, b) => (a.label > b.label ? 1 : -1)),
      }
    })
    .sort((a, b) => (a.label > b.label ? 1 : -1))

  useEffect(() => {
    setNodes(imageNodes)
    // Expandir automáticamente los nodos en móvil para mejor usabilidad
    if (isMobile && imageNodes.length > 0) {
      const expandedNodes = imageNodes.map((node) => {
        return {
          ...node,
          isExpanded: true,
        }
      })
      setNodes(expandedNodes)
    }
    // eslint-disable-next-line
  }, [grouped.length, isMobile])

  const onNodeCollapse = (node, [nodePath]) => {
    const newState = nodes.map((k, index) => {
      if (nodePath === index) {
        k.isExpanded = false
        return k
      }

      return k
    })

    setNodes(newState)
  }

  const onNodeExpand = (node, [nodePath]) => {
    const newState = nodes.map((k, index) => {
      if (nodePath === index) {
        k.isExpanded = true
        return k
      }

      return k
    })

    setNodes(newState)
  }

  // Modificar la función onNodeClick para mejorar la selección de imágenes en móvil
  const onNodeClick = (node, nodePath) => {
    // Verificar si es un nodo hoja (una imagen) o un nodo padre (una categoría)
    if (node.nodeData) {
      // Es un nodo hoja (una imagen)
      setCurrentReply(node.nodeData)

      // Actualizar el estado de selección en todos los nodos
      const newState = nodes.map((currentNode) => {
        if (!currentNode.hasOwnProperty("childNodes")) {
          return currentNode
        }

        const newChildren = currentNode.childNodes.map((child) => {
          // Resetear la selección para todos
          child.isSelected = false

          // Si este es el nodo que se hizo clic, marcarlo como seleccionado
          if (child.nodeData && child.nodeData.Id === node.nodeData.Id) {
            child.isSelected = true
          }

          return child
        })

        currentNode.childNodes = newChildren
        return currentNode
      })

      setNodes(newState)
    } else if (nodePath && nodePath.length > 0) {
      // Es un nodo padre (categoría) - manejar expansión/colapso
      const parentPath = nodePath[0]

      const newState = nodes.map((k, index) => {
        if (parentPath === index) {
          k.isExpanded = !k.isExpanded
        }
        return k
      })

      setNodes(newState)
    }
  }

  // Si no está abierto, no renderizar nada
  if (!props.isOpen) return null

  return (
    <div className="d-flex flex-column w-100 h-100 left-panel mobile-image-picker" style={{ left: "0", zIndex: 1000 }}>
      <div
        className="row d-flex flex-row align-items-center p-2 m-0"
        style={{ backgroundColor: "white", minHeight: "65px" }}
      >
        <i
          className="fas fa-arrow-left p-2 mx-3 my-1"
          style={{ fontSize: "1.5rem", cursor: "pointer" }}
          onClick={props.close}
          title="Volver sin enviar imagen"
        ></i>
        <div className="font-weight-bold">Enviar imagen</div>
      </div>
      <div className="container" style={{ height: isMobile ? "calc(100vh - 130px)" : "90vh", overflowY: "auto", padding: "20px" }}>
        {/* Listado de imágenes */}
        <div className="mb-4">
          <h5 className="mb-3">Imágenes predefinidas</h5>
          {isMobile ? (
            <ImageList
              items={nodes}
              onSelect={(nodeData) => {
                setCurrentReply(nodeData)
              }}
            />
          ) : (
            <Tree
              contents={nodes}
              onNodeClick={onNodeClick}
              onNodeExpand={onNodeExpand}
              onNodeCollapse={onNodeCollapse}
            />
          )}
        </div>

        {/* Vista previa al final */}
        {currentReply.ReplyLink && (
          <>
            <hr className="my-4" />
            <div className="mb-4">
              <h5 className="mb-3 text-success">
                <i className="fas fa-check-circle"></i> Vista previa
              </h5>
              <div className="text-center">
                <img
                  src={currentReply.ReplyLink || "/placeholder.svg"}
                  alt=""
                  className="img-fluid"
                  loading="lazy"
                  style={{
                    maxWidth: "800px",
                    maxHeight: isMobile ? "300px" : "500px",
                    objectFit: "contain",
                    border: "2px solid #28a745",
                    borderRadius: "8px",
                  }}
                  title="Imagen seleccionada"
                />
                <div className="mt-3">
                  <strong>Imagen seleccionada</strong>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal para vista ampliada de imagen */}
      <ImageModal />

      <div
        className="row d-flex flex-row align-items-center p-2 m-0 justify-content-center"
        style={{
          background: "rgba(219,219,219, 0.5)",
          minHeight: "65px",
          position: isMobile ? "fixed" : "relative",
          bottom: isMobile ? 0 : "auto",
          width: "100%",
        }}
      >
        <button
          onClick={() => props.onImageClick(currentReply.ReplyText, currentReply.ReplyLink)}
          disabled={!currentReply.ReplyLink}
          type="button"
          style={{ borderRadius: "25px" }}
          className="btn btn-success btn-icon"
        >
          <i className="fas fa-paper-plane mr-2"></i> Enviar
        </button>
      </div>
    </div>
  )
}
